import {useCallback} from 'react';

interface TiptapNode {
  type: string;
  attrs?: any;
  content?: TiptapNode[];
  text?: string;
  marks?: any[];
}

interface ListContext {
  type: 'ordered' | 'bullet';
  counters: number[];
  depth: number;
}

export function useRichTextToText() {
  return useCallback((jsonString: string | null | undefined): string | null => {
    if (!jsonString) {
      return null;
    }
    const json = JSON.parse(jsonString);

    const result: string[] = [];
    let previousNodeType: string | null = null;

    function processNode(node: TiptapNode, listContext: ListContext[] = []): void {
      if (
        previousNodeType &&
        (previousNodeType === 'bulletList' || previousNodeType === 'orderedList') &&
        (node.type === 'paragraph' || node.type === 'heading') &&
        listContext.length === 0
      ) {
        result.push('');
      }

      switch (node.type) {
        case 'doc':
          if (node.content) {
            node.content.forEach((child) => processNode(child, listContext));
          }
          break;

        case 'paragraph':
          if (!node.content || node.content.length === 0) {
            const indent = getIndentForListContext(listContext);
            result.push(indent);
          } else {
            const text = extractTextFromContent(node.content);
            const indent = getIndentForListContext(listContext);
            result.push(indent + text);
          }
          break;

        case 'heading':
          if (node.content) {
            const text = extractTextFromContent(node.content);
            const indent = getIndentForListContext(listContext);
            result.push(indent + text);
          }
          break;

        case 'bulletList':
          if (node.content) {
            const newContext: ListContext[] = [
              ...listContext,
              {
                type: 'bullet' as const,
                counters: [],
                depth: listContext.length,
              },
            ];
            node.content.forEach((listItem) => processListItem(listItem, newContext));
          }
          break;

        case 'orderedList':
          if (node.content) {
            const startNumber = node.attrs?.start || 1;
            const newContext: ListContext[] = [
              ...listContext,
              {
                type: 'ordered' as const,
                counters: [startNumber - 1],
                depth: listContext.length,
              },
            ];
            node.content.forEach((listItem) => processListItem(listItem, newContext));
          }
          break;

        default:
          if (node.content) {
            node.content.forEach((child) => processNode(child, listContext));
          }
          break;
      }

      previousNodeType = node.type;
    }

    function processListItem(listItem: TiptapNode, listContext: ListContext[]): void {
      if (!listItem.content) return;

      const currentContext = listContext[listContext.length - 1];
      if (currentContext.type === 'ordered') {
        // eslint-disable-next-line no-plusplus
        currentContext.counters[0]++;
      }

      let isFirstElement = true;

      listItem.content.forEach((child) => {
        if (child.type === 'paragraph') {
          const text = extractTextFromContent(child.content || []);
          const indent = getIndentForListContext(listContext.slice(0, -1));
          const marker = getListMarker(listContext);

          if (isFirstElement) {
            result.push(indent + marker + text);
            isFirstElement = false;
          } else {
            const continuationIndent = getIndentForListContext(listContext);
            result.push(continuationIndent + text);
          }
        } else if (child.type === 'bulletList' || child.type === 'orderedList') {
          processNode(child, listContext);
        } else {
          processNode(child, listContext);
        }
      });
    }

    function extractTextFromContent(content: TiptapNode[]): string {
      return content
        .map((node) => {
          if (node.type === 'text') {
            return node.text || '';
          }
          return '';
        })
        .join('');
    }

    function getIndentForListContext(listContext: ListContext[]): string {
      return '   '.repeat(listContext.length);
    }

    function getListMarker(listContext: ListContext[]): string {
      if (listContext.length === 0) return '';
      const currentContext = listContext[listContext.length - 1];
      if (currentContext.type === 'bullet') {
        return '- ';
      }
      return `${currentContext.counters[0]}. `;
    }

    processNode(json);

    return result.join('\n').replace(/\n+$/, '');
  }, []);
}
